<script lang="ts">
  import { copy } from '../lib/stores/copy';
  import Fooji from '@fooji/microsites';
  import { configStore } from '../lib/stores/config';
  import { isDemo } from '../lib/stores/mode';
  import { jwtDecode } from 'jwt-decode';
  import { notify } from '../lib/helpers/bugsnag';
  import { ErrorWithSetting } from '../lib/helpers/startWorkflow';
  import { nextStep, setPage, setLoading } from '../lib/stores/route';
  import PrizePopup from '../components/PrizePopup.svelte';
  import { saveUserAndStartWorkflow } from '../lib/helpers/auth';
  import { isPrimaryCampaign } from '../lib/constants/countries';

  let showPopup = false;

  const needsAuthentication = () => {
    const jwt = sessionStorage.getItem("jwt");

    if (jwt) {
      try {
        const { exp }: { exp: number } = jwtDecode(jwt);
        const safeExpire = exp - 15 * 60; // 15 minutes before expiration
        return safeExpire * 1000 < Date.now();
      } catch (error) {
        notify({ errorMessage: `Failed to decode JWT: ${jwt}`, error });
        sessionStorage.removeItem("jwt");
        return true;
      }
    }

    return true;
  }

  const authenticateIfNeeded = async () => {
    setLoading(true);

    if (needsAuthentication()) {
      try {
        // Authenticate with activision
        const returnURL = new URL(document.location.href);
        returnURL.searchParams.set("auth", "true");
        const redirect = await Fooji.Auth.activisionCOD.start($configStore.campaign, returnURL);

        document.location.href = redirect;
      } catch (error) {
        console.error('Error authenticating with Activision:', error);
        const isHandledError = error instanceof ErrorWithSetting && error.errorSetting && error.errorSetting !== "generic";

        if (!isHandledError) {
          notify({ errorMessage: 'Failed to authenticate with Activision', error });
        }

        setLoading(false);
      }
    } else {
      try {
        await saveUserAndStartWorkflow();
        nextStep();
      } catch (error) {
        console.error('Error starting workflow:', error);

        const isHandledError = error instanceof ErrorWithSetting && error.errorSetting && error.errorSetting !== "generic";

        if (!isHandledError) {
          notify({ errorMessage: 'Failed to start workflow', error });
        }

        const errorSetting = (error instanceof ErrorWithSetting) ? error.errorSetting : "generic";
        setPage("error", errorSetting);
      }

      setLoading(false);
    }
  }
    
  const onNextStep = async () => {
    if (!isDemo()) {
      await authenticateIfNeeded();
    } else {
      nextStep();
    }
  };
</script>

<!-- padding: 1rem;
    background-color: rgba(0, 0, 0, 0.5);
    border-radius: 0.25rem;
    margin-top: 1rem;
    margin-bottom: 1rem; -->

<main class="flex-col grow relative bg-dark bg-[url('/bg/mobile-bg1.webp')] tabletLg:bg-[url('/bg/main-bg.webp')] bg-cover bg-[center_calc(100%_+_40vw)] tabletLg:bg-center pb-[85vw] tabletLg:pb-[calc(118px+1rem)] laptop:pb-0">
  <div class="relative flex items-center flex-col grow justify-center px-[16px] text-center z-[1] tabletLg:w-[72vw] min-h-[47vw]">
    <div class="p-4 bg-black/50 rounded-md my-4 w-full tablet:w-auto">
      <h1 class="font-MrEavesXLSanNarOTUltra text-primary leading-[0.75] uppercase {$copy.homepage.titleTextClasses}"> <!-- [CHANGES-SM] text size classes moved to copy files -->
        {$copy.common.unlock}
        <span class="flex justify-center text-[0.84em] tracking-[0.01em] ml-[0.1em]">{$copy.common.dual2xp}</span>
      </h1>
      <h2 class="font-MrEavesXLSanNarOT text-[18px] text-white font-normal mb-[12px] leading-[0.75] mobile:text-[24px] tablet:text-[27px] laptop:text-[31px] desktop:text-[36px] desktopLg:text-[45px]">
        {$copy.homepage.for} <i class="font-MrEavesXLSanNarOTItalic">{$copy.homepage.blackOps6}</i>
      </h2>
      <p class="font-MrEavesXLSanNarOT text-[12px] text-white font-normal leading-[0.75] mobile:text-[14px] tablet:text-[18px] laptop:text-[20px] desktop:text-[22px] desktopLg:text-[28px]">
        {@html $copy.homepage.note}
      </p>
      {#if isPrimaryCampaign}
      <p class="font-MrEavesXLSanNarOTUltra text-white font-black leading-[0.9] uppercase text-[16px] mt-[24px] mobile:text-[20px] tablet:text-[24px] laptop:text-[26px] desktop:text-[30px] desktopLg:text-[37px]">
        <span class="font-LCScript text-primary capitalize leading-[0.75]">{@html $copy.prizeNames.plus}</span>
          {@html $copy.homepage.bonus}
        </p>
      {/if}
      <div class="flex flex-col w-full" class:flex-col-reverse={!isPrimaryCampaign}>
        <form class="mt-[24px]">
          <div class="flex flex-col items-center">
            <div class="relative flex items-center justify-center w-full max-w-[360px] tablet:max-w-[450px]">
              <button
                on:click|preventDefault={onNextStep}
                class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[16px] whitespace-nowrap text-white px-[12px] uppercase h-[42px] min-w-[132px] bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden tablet:text-[18px] tablet:h-[48px]">
                <span class="relative z-[1]">
                  {$copy.homepage.entry.button}
                </span>
              </button>
            </div>
          </div>
        </form>
        <div class="mt-[18px] text-center">
          {#if isPrimaryCampaign}
            <div class="font-MrEavesXLSanNarOT text-[19px] text-white mb-[24px] leading-[0.9]">
              {@html $copy.homepage.entry.rules}
            </div>
          {/if}
          <div class="flex justify-center items-center">
            <div class="std-popup__inner relative" class:is-open={showPopup}>
              <button
                on:click={() => showPopup = !showPopup}
                class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[16px] whitespace-nowrap text-white px-[12px] uppercase h-[42px] min-w-[132px] bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden tablet:text-[18px] tablet:h-[48px]">
                <span class="relative z-[1]">
                  {$copy.homepage.rewardsButton}
                </span>
              </button>
              <PrizePopup bind:showPopup={showPopup} />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</main>
